import React, { useEffect, useState } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/Button Triangle.png";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import LeftCircle from "../../assets/Icons/Left circle B.png";
import RightCircle from "../../assets/Icons/Right circle A.png";
import FilterIcon from "../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../assets/Icons/cross_svg.svg";
import Dropdown from "../Dropdown";

const profileOptions = [
  { value: "CUSTOMER", label: "LMBP" },
  { value: "SUB_LMBP", label: "Sub-LMBP" },
  { value: "OPERATION", label: "Operation" },
  { value: "FRANCHISE", label: "Franchise" },
  { value: "HUB", label: "Hub" },
];

const statusOptions = [
  { value: "1", label: "Active" },
  { value: "4", label: "In-Active" },
  { value: "2", label: "New User" },
  { value: "3", label: "Blocked" },
  { value: "5", label: "Awaiting Customer Authorization" },
];

const UserFilterModal = ({
  show,
  handleClose,
  setUserData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      ...filterData,
      userProfile: filterData.userProfile || null,
      status: filterData.status || null,
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
      name: filterData.name || "",
      emailId: filterData.emailId || "",
      mobileNo: filterData.mobileNo || "",
    },
    mode: "onChange",
  });

  // Watch all form values to check if a filter has a value
  const watchedValues = watch();

  // Function to check if a tab has an active filter value
  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "User Id":
        return watchedValues.userId && watchedValues.userId.trim() !== "";
      case "Profile":
        return (
          (watchedValues.userProfile && watchedValues.userProfile.value !== "") ||
          (watchedValues.name && watchedValues.name.trim() !== "")
        );
      case "Status":
        return watchedValues.status && watchedValues.status.value !== "";
      case "Created Date":
        return (
          (watchedValues.fromDate && watchedValues.fromDate !== "") ||
          (watchedValues.toDate && watchedValues.toDate !== "")
        );
      case "Name":
        return watchedValues.name && watchedValues.name.trim() !== "";
      case "User Email Id":
        return watchedValues.emailId && watchedValues.emailId.trim() !== "";
      case "User Mobile No":
        return watchedValues.mobileNo && watchedValues.mobileNo.trim() !== "";
      default:
        return false;
    }
  };

  const loggedInUserName = sessionStorage.getItem("username");

  const [selectedTab, setSelectedTab] = useState("User Id");
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [emailError, setEmailError] = useState(""); // State for email validation error
  const [mobileError, setMobileError] = useState(""); // State for mobile validation error

  const fetchUsersByProfile = async (profileValue) => {
    if (!profileValue) {
      setUserList([]);
      return;
    }
    setIsLoadingUsers(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        userId: "",
        userProfile: profileValue,
        status: "",
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_USER_DATA}?pageSize=100&pageNo=1`,
        requestBody
      );

      if (response?.status === 200 && response?.data?.data) {
        const rawUsers = response?.data?.data || [];
        const sortedUsers = [...rawUsers].sort((a, b) =>
          (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, {
            sensitivity: "base",
          })
        );
        setUserList(sortedUsers);
      } else {
        setUserList([]);
      }
    } catch (error) {
      console.error("Error fetching users by profile:", error);
      setUserList([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleProfileDropdownChange = (selectedVal) => {
    const selected = profileOptions.find((opt) => opt.value === selectedVal);
    setValue("userProfile", selected || null, { shouldValidate: true });
    setValue("name", "", { shouldValidate: true }); // Clear selected user name on profile change
    setFilterData((prev) => ({
      ...prev,
      userProfile: selected || null,
      name: "",
    }));
    setUserList([]);
    if (selectedVal) {
      fetchUsersByProfile(selectedVal);
    }
  };

  useEffect(() => {
    if (show) {
      reset({
        ...filterData,
        userProfile: filterData.userProfile || null,
        status: filterData.status || null,
        fromDate: filterData.fromDate || "",
        toDate: filterData.toDate || "",
        name: filterData.name || "",
        emailId: filterData.emailId || "",
        mobileNo: filterData.mobileNo || "",
      });
      if (filterData.userProfile?.value) {
        fetchUsersByProfile(filterData.userProfile.value);
      } else {
        setUserList([]);
      }
    }
  }, [show]);

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
    // Clear email error when switching tabs
    if (tabName !== "User Email Id") {
      setEmailError("");
    }
    // Clear mobile error when switching tabs
    if (tabName !== "User Mobile No") {
      setMobileError("");
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const validateEmail = (email) => {
    if (!email) {
      return "Email is required";
    } else if (/\s/.test(email)) {
      // Partial emails are allowed: the search matches any part of the address
      return "Email search cannot contain spaces";
    }
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile) {
      return "Mobile Number is required";
    } else if (!/^\d{1,10}$/.test(mobile)) {
      // Partial numbers are allowed: the search matches any part of the number
      return "Mobile Number can contain only digits";
    }
    return "";
  };

  const onSubmit = (data) => {
    // Validate email when form is submitted and email tab is active or email field has value
    if (selectedTab === "User Email Id" || data.emailId) {
      const emailValidationError = validateEmail(data.emailId);
      if (emailValidationError) {
        setEmailError(emailValidationError);
        return; // Prevent form submission if email is invalid
      }
    }

    // Validate mobile when form is submitted and mobile tab is active or mobile field has value
    if (selectedTab === "User Mobile No" || data.mobileNo) {
      const mobileValidationError = validateMobile(data.mobileNo);
      if (mobileValidationError) {
        setMobileError(mobileValidationError);
        return; // Prevent form submission if mobile is invalid
      }
    }

    setEmailError(""); // Clear any previous email errors
    setMobileError(""); // Clear any previous mobile errors
    setFilterData(data);
    fetchCustomerData(1, data);
  };

  const fetchCustomerData = async (page, formData) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        userId: formData.userId || "",
        userProfile: formData.userProfile?.value || "",
        status: formData.status?.value || "",
        fromDate: formatDate(formData.fromDate),
        toDate: formatDate(formData.toDate),
        name: formData.name || "",
        emailId: formData.emailId || "",
        mobileNo: formData.mobileNo || "",
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_USER_DATA}?pageSize=${pageSize}&pageNo=${page}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setUserData(modifiedData);
        setNoData(modifiedData?.length === 0);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        setUserData([]);
        setNoData(true);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((customer, index) => ({
      ...customer,
      id: index + 1,
      address:
        customer.address1 + ", " + customer.address2 + ", " + customer.address3,
      roleName: customer?.roleName === "Customer" ? "LMBP" : customer?.roleName,
      userProfileDisplay:
        customer?.userProfile === "CUSTOMER"
          ? "LMBP"
          : customer?.userProfile === "SUB_LMBP" ||
            customer?.userProfile === "SUB-LMBP"
          ? "Sub-LMBP"
          : customer?.userProfile,
    }));
  };

  const onReset = () => {
    reset({
      userId: "",
      userProfile: null,
      status: null,
      fromDate: "",
      toDate: "",
      name: "",
      emailId: "",
      mobileNo: "",
    });
    setFilterData({
      userId: "",
      userProfile: null,
      status: null,
      fromDate: "",
      toDate: "",
      name: "",
      emailId: "",
      mobileNo: "",
    });
    setUserList([]);
    setEmailError(""); // Clear email error on reset
    setMobileError(""); // Clear mobile error on reset
    handleReset();
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="leftImg"
                className="absolute right-0 top-0 w-[110px] h-[105px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title font-medium">
                    Filters
                  </h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="filter icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {[
                        "User Id",
                        "Name",
                        "Profile",
                        "Status",
                        "Created Date",
                        "User Email Id",
                        "User Mobile No",
                      ].map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content">
                      {selectedTab === "User Id" && (
                        <Controller
                          name="userId"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">User ID</label>
                              <input
                                {...field}
                                type="text"
                                placeholder="Enter User ID"
                                className="dateInput"
                                maxLength={20}
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^a-zA-Z0-9]/g,
                                    "",
                                  );
                                  field.onChange(val);
                                  setFilterData((prev) => ({
                                    ...prev,
                                    userId: val,
                                  }));
                                }}
                              />
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Name" && (
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Name</label>
                              <input
                                {...field}
                                value={field.value || ""}
                                type="text"
                                placeholder="Type any part of the name"
                                className="dateInput"
                                maxLength={100}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  setFilterData((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }));
                                }}
                              />
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Profile" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                          }}
                        >
                          <Controller
                            name="userProfile"
                            control={control}
                            render={({ field }) => (
                              <div className="dateField">
                                <label className="dateLabel">User Profile</label>
                                <div className="dateInputWrapper">
                                  <select
                                    value={field.value?.value || ""}
                                    onChange={(e) =>
                                      handleProfileDropdownChange(e.target.value)
                                    }
                                    className="dateInput"
                                  >
                                    <option value="">Select Profile</option>
                                    {profileOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            )}
                          />

                          <Controller
                            name="name"
                            control={control}
                            render={({ field }) => {
                              const sortedList = [...userList].sort((a, b) =>
                                (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, {
                                  sensitivity: "base",
                                })
                              );
                              const userOptions = [
                                ...(field.value
                                  ? [{ label: "✕ Clear User Selection", value: "" }]
                                  : []),
                                ...sortedList.map((user) => ({
                                  label: `${user.name} (${user.userId})`,
                                  value: user.name,
                                })),
                              ];

                              return (
                                <div className="dateField">
                                  <label className="dateLabel">User Name</label>
                                  <div className="dateInputWrapper">
                                    <Dropdown
                                      placeholder={
                                        isLoadingUsers
                                          ? "Loading users..."
                                          : !watchedValues.userProfile?.value
                                          ? "Select Profile First"
                                          : "Select User Name"
                                      }
                                      disabled={
                                        !watchedValues.userProfile?.value ||
                                        isLoadingUsers
                                      }
                                      options={userOptions}
                                      value={field.value}
                                      labelKey="label"
                                      valueKey="value"
                                      showSearch={true}
                                      selectClassName="!h-[38px] !rounded-[6px]"
                                      onChange={(item) => {
                                        const selectedName = item ? item.value : "";
                                        field.onChange(selectedName);
                                        setFilterData((prev) => ({
                                          ...prev,
                                          name: selectedName,
                                        }));
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            }}
                          />
                        </div>
                      )}

                      {selectedTab === "Status" && (
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Status</label>
                              <div className="dateInputWrapper">
                                <select
                                  {...field}
                                  value={field.value?.value || ""}
                                  onChange={(e) => {
                                    const selected = statusOptions.find(
                                      (opt) => opt.value === e.target.value,
                                    );
                                    field.onChange(selected || null);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      status: selected || null,
                                    }));
                                  }}
                                  className="dateInput"
                                >
                                  <option value="">Select Status</option>
                                  {statusOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value, formValues) =>
                                  !value ||
                                  !formValues.toDate ||
                                  new Date(value) <=
                                    new Date(formValues.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, formValues) =>
                                  !formValues.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        fromDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value, formValues) =>
                                  !value ||
                                  !formValues.fromDate ||
                                  new Date(value) >=
                                    new Date(formValues.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, formValues) =>
                                  !formValues.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        toDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "User Email Id" && (
                        <Controller
                          name="emailId"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">User Email Id</label>
                              <input
                                {...field}
                                type="text"
                                placeholder="Type any part of the email"
                                className="dateInput"
                                maxLength={100}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  setFilterData((prev) => ({
                                    ...prev,
                                    emailId: e.target.value,
                                  }));
                                  // Validate email on change when in email tab
                                  if (selectedTab === "User Email Id") {
                                    const error = validateEmail(e.target.value);
                                    setEmailError(error);
                                  }
                                }}
                                onBlur={(e) => {
                                  // Validate email on blur
                                  const error = validateEmail(e.target.value);
                                  setEmailError(error);
                                }}
                              />
                              {emailError && (
                                <div
                                  className="error-text"
                                  style={{
                                    color: "#d32f2f",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                    display: "block",
                                  }}
                                >
                                  {emailError}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "User Mobile No" && (
                        <Controller
                          name="mobileNo"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">
                                User Mobile No
                              </label>
                              <input
                                {...field}
                                type="tel"
                                placeholder="Type any part of the mobile no"
                                className="dateInput"
                                maxLength={10}
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^0-9]/g,
                                    "",
                                  );
                                  field.onChange(val);
                                  setFilterData((prev) => ({
                                    ...prev,
                                    mobileNo: val,
                                  }));
                                  // Validate mobile on change when in mobile tab
                                  if (selectedTab === "User Mobile No") {
                                    const error = validateMobile(val);
                                    setMobileError(error);
                                  }
                                }}
                                onBlur={(e) => {
                                  // Validate mobile on blur
                                  const error = validateMobile(e.target.value);
                                  setMobileError(error);
                                }}
                              />
                              {mobileError && (
                                <div
                                  className="error-text"
                                  style={{
                                    color: "#d32f2f",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                    display: "block",
                                  }}
                                >
                                  {mobileError}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back w-[133px] !rounded-lg"
                      onClick={onReset}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit w-[133px] !rounded-lg"
                      disabled={
                        isSubmitting ||
                        (selectedTab === "User Email Id" && emailError) ||
                        (selectedTab === "User Mobile No" && mobileError)
                      }
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserFilterModal;
